import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { parseJsonProperty } from "@/lib/utils"
import { useRentFacilities } from "@/hooks/rent/useRentFacilities"
import { useShowcasePrices } from "@/hooks/prices/useShowcasePrices"
import { useAuth } from "@/auth/useAuth"
import {
  RENT_STEPS,
  buildRentPaymentPlanOptions,
  type PersonalPhase,
  type RentPeriod,
  type RentStepCode,
} from "@/components/rent/rent-constants"
import type { PersonalInfoFormHandle } from "@/components/rent/personal-info-form"
import type { CustomerSelectFormHandle } from "@/components/rent/customer-select-form"
import { ContractsApi } from "@/lib/contracts-api"
import { InvoiceApi } from "@/lib/invoices-api"
import { getApiErrorMessage } from "@/lib/error-codes"
import { getShowcaseErrorMessage } from "@/lib/price-errors"
import { queryKeys } from "@/config/query-keys"
import { RENT_SHOWCASE_PERIODS } from "@/types/price"
import type { Contract } from "@/types/contract"
import type { Invoice } from "@/types/invoice"

function getIsNextDisabled(
  step: RentStepCode,
  options: {
    selectedFacilityCode: string | null
    selectedUnitTypeCode: string | null
    personalPhase: PersonalPhase
    isAuthLoading: boolean
    selectedCustomerId: string
    startedAt: string
    period: RentPeriod | ""
    isPaymentSubmitting: boolean
    hasSelectedPrice: boolean
    isPricingLoading: boolean
    hasPricingError: boolean
  },
): boolean {
  switch (step) {
    case "warehouse":
      return !options.selectedFacilityCode
    case "box":
      return !options.selectedUnitTypeCode
    case "personal":
      return options.personalPhase === "auth"
        ? options.isAuthLoading
        : !options.selectedCustomerId
    case "payment":
      return (
        !options.startedAt ||
        !options.period ||
        options.isPaymentSubmitting ||
        options.isPricingLoading ||
        options.hasPricingError ||
        !options.hasSelectedPrice
      )
    default:
      return false
  }
}

export function useRentFlow() {
  const queryClient = useQueryClient()
  const { isAuthenticated, isLoading: isAuthLoading, customers, selectedCustomer } = useAuth()
  const { availableFacilities, isLoading: isFacilitiesLoading } = useRentFacilities()

  const personalFormRef = useRef<PersonalInfoFormHandle>(null)
  const customerSelectRef = useRef<CustomerSelectFormHandle>(null)
  const unitTypeDetailRef = useRef<HTMLDivElement>(null)

  const [activeStep, setActiveStep] = useState<RentStepCode>("warehouse")
  const [personalTab, setPersonalTab] = useState<"register" | "login">("register")
  const [personalPhase, setPersonalPhase] = useState<PersonalPhase>("auth")
  const [selectedCustomerId, setSelectedCustomerId] = useState("")
  const [selectedFacilityCode, setSelectedFacilityCode] = useState<string | null>(null)
  const [selectedUnitTypeCode, setSelectedUnitTypeCode] = useState<string | null>(null)
  const [startedAt, setStartedAt] = useState("")
  const [period, setPeriod] = useState<RentPeriod | "">("")
  const [promoDraft, setPromoDraft] = useState("")
  const [appliedPromo, setAppliedPromo] = useState("")
  const [isRecurrent, setIsRecurrent] = useState(false)
  const [isPaymentSubmitting, setIsPaymentSubmitting] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [createdContract, setCreatedContract] = useState<Contract | null>(null)
  // What the contract was created for: paying again for the same choice pays that contract, not a second one.
  const [createdFor, setCreatedFor] = useState("")
  const [invoicesToPay, setInvoicesToPay] = useState<Invoice[]>([])
  const [isPayDialogOpen, setIsPayDialogOpen] = useState(false)

  const [prevIsAuthenticated, setPrevIsAuthenticated] = useState(isAuthenticated)
  if (isAuthenticated !== prevIsAuthenticated) {
    if (!isAuthenticated && activeStep === "personal") {
      setPersonalPhase("auth")
      setSelectedCustomerId("")
    }
    setPrevIsAuthenticated(isAuthenticated)
  }

  const stepIndex = RENT_STEPS.findIndex((step) => step.code === activeStep)

  const tabs = RENT_STEPS.map((step, index) => ({
    ...step,
    checked: index < stepIndex,
    disabled: index > stepIndex,
  }))

  if (
    selectedFacilityCode &&
    !isFacilitiesLoading &&
    !availableFacilities.some((item) => item.facility.code === selectedFacilityCode)
  ) {
    setSelectedFacilityCode(null)
    setSelectedUnitTypeCode(null)
  }

  const selectedFacility = useMemo(
    () => availableFacilities.find((item) => item.facility.code === selectedFacilityCode),
    [availableFacilities, selectedFacilityCode],
  )

  if (
    selectedUnitTypeCode &&
    selectedFacility &&
    !selectedFacility.unitTypes.some((unitType) => unitType.code === selectedUnitTypeCode)
  ) {
    setSelectedUnitTypeCode(null)
  }

  const selectedUnitType = useMemo(
    () => selectedFacility?.unitTypes.find((unitType) => unitType.code === selectedUnitTypeCode),
    [selectedFacility?.unitTypes, selectedUnitTypeCode],
  )

  const selectedFacilityCapabilities = parseJsonProperty(
    selectedFacility?.facility.properties?.find((property) => property.code === "capabilities")
      ?.value,
    [] as string[],
  )

  const showcaseParams = useMemo(
    () => ({
      productKind: "unit" as const,
      facility: selectedFacilityCode ?? undefined,
      productCode: selectedUnitTypeCode ?? undefined,
      startedAt: startedAt || undefined,
      promo: appliedPromo || undefined,
      showcase: [...RENT_SHOWCASE_PERIODS],
    }),
    [appliedPromo, selectedFacilityCode, selectedUnitTypeCode, startedAt],
  )

  const showcaseEnabled = Boolean(
    selectedFacilityCode && selectedUnitTypeCode && startedAt,
  )

  const {
    data: showcaseData,
    isLoading: isShowcaseLoading,
    isFetching: isShowcaseFetching,
    errorKind: showcaseErrorKind,
    error: showcaseError,
    selectedPrice,
  } = useShowcasePrices(showcaseParams, {
    enabled: showcaseEnabled,
    selectedPeriod: period || undefined,
  })

  const paymentPlanOptions = useMemo(
    () => buildRentPaymentPlanOptions(showcaseData?.items ?? []),
    [showcaseData?.items],
  )

  const pricingErrorMessage = showcaseErrorKind
    ? getShowcaseErrorMessage(showcaseErrorKind, showcaseError?.message)
    : null

  const promoErrorMessage =
    showcaseErrorKind === "promo_not_found" || showcaseErrorKind === "promo_invalid"
      ? pricingErrorMessage
      : null

  const planErrorMessage =
    showcaseErrorKind && showcaseErrorKind !== "promo_not_found" && showcaseErrorKind !== "promo_invalid"
      ? pricingErrorMessage
      : null

  useEffect(() => {
    if (!period || !showcaseData?.items) {
      return
    }

    const stillAvailable = showcaseData.items.some((item) => item.period === period)
    if (!stillAvailable) {
      setPeriod("")
    }
  }, [period, showcaseData?.items])

  const syncPersonalStepState = useCallback(() => {
    if (isAuthenticated) {
      setPersonalPhase("customer")
      setSelectedCustomerId(
        selectedCustomer?.customerId ? String(selectedCustomer.customerId) : "",
      )
      return
    }

    setPersonalPhase("auth")
    setSelectedCustomerId("")
  }, [isAuthenticated, selectedCustomer])

  useEffect(() => {
    if (isAuthenticated) {
      syncPersonalStepState()
    }
  }, [isAuthenticated, syncPersonalStepState])

  const goToStep = useCallback(
    (step: RentStepCode) => {
      if (step === "personal") {
        syncPersonalStepState()
      }
      setActiveStep(step)
    },
    [syncPersonalStepState],
  )

  const goToNextStep = useCallback(() => {
    const nextStep = RENT_STEPS[stepIndex + 1]
    if (nextStep) {
      goToStep(nextStep.code)
    }
  }, [goToStep, stepIndex])

  const handleFacilitySelect = useCallback((code: string | null) => {
    setSelectedFacilityCode(code)
    setSelectedUnitTypeCode(null)
  }, [])

  const handleStepChange = useCallback(
    (step: string) => {
      const targetIndex = RENT_STEPS.findIndex((item) => item.code === step)
      if (targetIndex !== -1 && targetIndex <= stepIndex) {
        goToStep(RENT_STEPS[targetIndex].code)
      }
    },
    [goToStep, stepIndex],
  )

  const handleStartedAtChange = useCallback((value: string) => {
    setStartedAt(value)
    setPeriod("")
  }, [])

  const handleApplyPromo = useCallback(() => {
    const nextPromo = promoDraft.trim().toUpperCase()
    setAppliedPromo(nextPromo)
    setPaymentError(null)
  }, [promoDraft])

  const handlePromoDraftChange = useCallback((value: string) => {
    setPromoDraft(value)
    if (!value.trim() && appliedPromo) {
      setAppliedPromo("")
    }
  }, [appliedPromo])

  const handlePaymentSubmit = useCallback(async () => {
    if (!selectedFacilityCode || !selectedUnitTypeCode) {
      setPaymentError("Выберите склад и размер бокса")
      return
    }

    if (!startedAt || !period) {
      setPaymentError("Выберите дату заезда и тариф")
      return
    }

    if (!selectedPrice) {
      setPaymentError("Не удалось рассчитать стоимость выбранного тарифа")
      return
    }

    setPaymentError(null)
    setIsPaymentSubmitting(true)

    const choice = [selectedFacilityCode, selectedUnitTypeCode, startedAt, period, appliedPromo].join("|")

    try {
      const contract =
        createdContract && createdFor === choice
          ? createdContract
          : await ContractsApi.createContract({
              facility: selectedFacilityCode,
              unitType: selectedUnitTypeCode,
              startedAt,
              period,
              promo: appliedPromo,
              recurrent: isRecurrent,
            })
      setCreatedContract(contract)
      setCreatedFor(choice)

      const customerId = selectedCustomer?.customerId
      if (customerId != null) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all(customerId) })
        await queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all(customerId) })
      }

      const invoicesResponse = await InvoiceApi.getInvoicesByContractId(contract.id)
      const pendingInvoices = invoicesResponse.items.filter((invoice) => invoice.status === "pending")
      const invoices = pendingInvoices.length > 0 ? pendingInvoices : invoicesResponse.items

      if (invoices.length === 0) {
        setPaymentError("Договор создан, но счета для оплаты не найдены")
        return
      }

      setInvoicesToPay(invoices)
      setIsPayDialogOpen(true)
    } catch (error) {
      setPaymentError(getApiErrorMessage(error))
    } finally {
      setIsPaymentSubmitting(false)
    }
  }, [
    appliedPromo,
    createdContract,
    createdFor,
    isRecurrent,
    period,
    queryClient,
    selectedCustomer,
    selectedFacilityCode,
    selectedPrice,
    selectedUnitTypeCode,
    startedAt,
  ])

  const handleNext = useCallback(async () => {
    if (activeStep === "personal") {
      if (personalPhase === "auth") {
        const result = await personalFormRef.current?.submit()
        if (!result?.success) {
          return
        }

        if (result.needsCustomerSelection) {
          setPersonalPhase("customer")
          return
        }

        goToNextStep()
        return
      }

      const success = await customerSelectRef.current?.submit()
      if (!success) {
        return
      }

      goToNextStep()
      return
    }

    if (activeStep === "payment") {
      await handlePaymentSubmit()
      return
    }

    goToNextStep()
  }, [activeStep, goToNextStep, handlePaymentSubmit, personalPhase])

  const handleBack = useCallback(() => {
    const prevStep = RENT_STEPS[stepIndex - 1]
    if (prevStep) {
      goToStep(prevStep.code)
    }
  }, [goToStep, stepIndex])

  const handlePayDialogOpenChange = useCallback((open: boolean) => {
    setIsPayDialogOpen(open)
    if (!open) {
      setInvoicesToPay([])
    }
  }, [])

  const isPricingLoading = showcaseEnabled && isShowcaseLoading
  const isPromoApplying = Boolean(appliedPromo) && isShowcaseFetching
  const hasPricingError = Boolean(showcaseErrorKind)

  const isNextDisabled = getIsNextDisabled(activeStep, {
    selectedFacilityCode,
    selectedUnitTypeCode,
    personalPhase,
    isAuthLoading,
    selectedCustomerId,
    startedAt,
    period,
    isPaymentSubmitting,
    hasSelectedPrice: Boolean(selectedPrice),
    isPricingLoading,
    hasPricingError,
  })

  const isLastStep = stepIndex === RENT_STEPS.length - 1

  const personalSideTitle =
    personalPhase === "customer"
      ? customers.length === 1
        ? "Ваш профиль"
        : "Выберите профиль"
      : personalTab === "login"
        ? "Войдите в личный кабинет"
        : "Заполните личные данные"

  return {
    activeStep,
    tabs,
    stepIndex,
    isLastStep,
    isNextDisabled,
    handleStepChange,
    handleNext,
    handleBack,
    personalFormRef,
    customerSelectRef,
    unitTypeDetailRef,
    personalTab,
    setPersonalTab,
    personalPhase,
    selectedCustomerId,
    setSelectedCustomerId,
    customers,
    availableFacilities,
    isFacilitiesLoading,
    selectedFacilityCode,
    selectedFacility,
    selectedUnitTypeCode,
    setSelectedUnitTypeCode,
    selectedUnitType,
    selectedFacilityCapabilities,
    handleFacilitySelect,
    personalSideTitle,
    startedAt,
    setStartedAt: handleStartedAtChange,
    period,
    setPeriod,
    promoDraft,
    setPromoDraft: handlePromoDraftChange,
    appliedPromo,
    handleApplyPromo,
    isRecurrent,
    setIsRecurrent,
    paymentPlanOptions,
    isPricingLoading,
    isPromoApplying,
    planErrorMessage,
    promoErrorMessage,
    selectedPrice,
    paymentError,
    isPaymentSubmitting,
    createdContract,
    invoicesToPay,
    isPayDialogOpen,
    handlePayDialogOpenChange,
  }
}
